import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { CertificateSection } from "../components/CertificateSection";
import { VideoModal } from "../components/VideoModal";
import { courses } from "../data/mock";
import { coursePresentation, getPackageCourses, courseWordForm } from "../data/coursePresentation";
import { CoursePackageSection, CourseLearningSection, CoursePurchaseSection } from "../components/course/CourseDetailsSection";
import { 
  ArrowRight, ShoppingCart, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { MoneyBackGuarantee } from "../components/MoneyBackGuarantee";
import { CourseHero } from "../components/course/CourseHero";
import "../course-paper.css";
import SEO from "../components/SEO";
import { courseLanding } from '../data/courseLanding';
import { CourseTasksSection, CourseReadinessSection, CourseAuthorSection, CourseOfferSection, CourseFAQSection } from '../components/course/CourseSalesSections';

// Course-specific components
import {
  AboutCourseSection,
  SkillsSection,
  ProjectsSection,
  RelatedCoursesSection,
  CourseTestimonialsSection,
  CurriculumSection
} from "../components/course";

// Превращает URL в тексте примечания в кликабельные ссылки, сохраняя переносы строк
const renderNoteWithLinks = (text) => {
  if (!text) return null;
  return text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="editorial-text-link break-all"
      >
        {part}
      </a>
    ) : (
      part
    )
  );
};

export const CoursePage = () => {
  const { slug } = useParams();
  const course = courses.find(c => c.slug === slug);
  const presentation = coursePresentation[slug];
  const landing = courseLanding[slug];
  const included = getPackageCourses(course || {}, courses);
  const { addToCart, isInCart, setShowCart } = useCart();
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const heroRef = useRef(null);

  // Show sticky bar when hero section is scrolled out of view
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowStickyBar(heroBottom < 100);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [slug]);

  if (!course) {
    return (
      <div className="min-h-screen bg-white editorial-course-page">
        <SEO title="Курс не найден" description="Такого курса нет. Посмотрите каталог курсов." noindex />
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Курс не найден</h1>
            <Link to="/" className="text-teal-600 hover:text-teal-500">
              Вернуться на главную
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const modules = course.curriculum?.modules || (Array.isArray(course.curriculum) ? course.curriculum : []);
  const courseNavLinks = [
    ...(landing ? [{ label: landing.tasks ? 'Практика' : 'Проекты', href: '#course-projects' }] : []),
    ...(included.length || modules.length ? [{ label: included.length ? "Состав пакета" : "Программа", href: "#course-program" }] : []),
    ...(!landing && course.projects?.length ? [{ label: "Проекты", href: "#course-projects" }] : []),
    ...(course.testimonials?.length ? [{ label: "Отзывы", href: "#course-reviews" }] : []),
    { label: "Обучение", href: "#course-learning" },
    { label: landing ? 'Вопросы' : 'Сертификат', href: landing ? '#course-faq' : '#course-certificate' },
    { label: "Блог", href: "/blog", isRoute: true }
  ];

  return (
    <div className="min-h-screen bg-white editorial-course-page">
      <SEO 
        title={course.title}
        description={course.description}
        image={course.image}
        url={`/course/${course.slug}`}
        type="product"
        course={{
          title: course.title,
          description: course.description,
          price: course.price,
          duration: course.duration,
          totalHours: course.totalHours,
          rating: course.rating,
          students: course.students,
          reviewPlatforms: course.reviewPlatforms,
          testimonials: course.testimonials
        }}
      />
      <Header links={courseNavLinks} />
      
      <CourseHero course={course} presentation={presentation} landing={landing} heroRef={heroRef} onPlay={() => setShowVideoModal(true)} renderNote={renderNoteWithLinks} />

      {landing && (landing.tasks ? <CourseTasksSection landing={landing}/> : <ProjectsSection projects={course.projects} landing={landing} />)}

      {/* About Course Section */}
      {landing ? <CourseReadinessSection landing={landing} /> : <AboutCourseSection aboutSection={{ targetAudience: presentation?.targetAudience || course.aboutSection?.targetAudience }} />}

      {/* Career Section */}
      {/* Career figures remain unpublished until dated sources are verified. */}

      {/* Skills Section */}
      <SkillsSection outcomes={presentation?.outcomes} category={course.category} />

      {/* Projects Section */}
      {!landing && <ProjectsSection projects={course.projects} />}

      {/* Curriculum Section */}
      {included.length ? <CoursePackageSection key={`package-${slug}`} included={included} /> : <CurriculumSection
        key={`curriculum-${slug}`}
        curriculum={course.curriculum}
        courseImage={course.image}
        courseTitle={presentation?.displayTitle || course.title}
        courseDescription={landing?.intro || presentation?.intro || course.description}
        noteTitle={landing ? landing.noteTitle || 'Три проекта: от задачи до запуска' : undefined}
      />}

      {/* Testimonials Section */}
      {landing && <CourseAuthorSection />}
      <CourseTestimonialsSection key={`reviews-${slug}`} testimonials={course.testimonials} />

      <CourseLearningSection course={course} />
      <CertificateSection id="course-certificate" />
      {landing ? <><CourseOfferSection course={course} landing={landing}/><CourseFAQSection landing={landing}/></> : <CoursePurchaseSection course={course} displayTitle={presentation?.displayTitle} />}
      <RelatedCoursesSection course={course} />

      {/* Sticky Buy Bar */}
      <div 
        className={`fixed bottom-0 left-0 right-0 bg-[#F1EDE4] border-t border-gray-700 shadow-2xl z-50 transition-transform duration-300 ${
          showStickyBar ? 'translate-y-0' : 'translate-y-full invisible'
        }`}
        data-testid="sticky-buy-bar"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Course Info */}
            <div className="hidden sm:flex items-center gap-4 flex-1 min-w-0">
              <img loading="lazy" 
                src={course.image} 
                alt={course.title}
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <h3 className="text-white font-semibold truncate">{course.title}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>{course.lessons} {courseWordForm(course.lessons, ["урок", "урока", "уроков"])}</span>
                  {course.totalHours > 0 && <><span>·</span><span>{course.totalHours.toLocaleString("ru-RU")} ч видео</span></>}
                </div>
              </div>
            </div>

            {/* Price and CTA */}
            <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-end">
              <div className="flex flex-col items-start sm:items-end gap-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl sm:text-3xl font-bold text-white">
                    {course.price.toLocaleString()} ₽
                  </span>
                  {course.originalPrice && (
                    <span className="text-sm sm:text-base text-gray-500 line-through">
                      {course.originalPrice.toLocaleString()} ₽
                    </span>
                  )}
                </div>
                <MoneyBackGuarantee days={30} compact />
              </div>
              
              {isInCart(course.slug) ? (
                <Button 
                  onClick={() => setShowCart(true)}
                  className="paper-primary"
                  data-testid="sticky-in-cart-btn"
                >
                  <Check className="mr-2" size={18} />
                  <span className="hidden sm:inline">В корзине —</span> Оформить
                </Button>
              ) : (
                <Button 
                  onClick={() => addToCart({
                    slug: course.slug,
                    title: course.title,
                    price: course.price,
                    image: course.image,
                    skillspaceCourses: course.skillspaceCourses || []
                  })}
                  className="paper-primary"
                  data-testid="sticky-buy-btn"
                >
                  <ShoppingCart className="mr-2" size={18} />
                  Купить курс
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform hidden sm:inline" size={18} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Video Modal */}
      {course.promoVideo && (
        <VideoModal 
          videoUrl={landing?.video || course.promoVideo}
          poster={landing?.poster}
          label={landing ? 'Реклама курса — AI-разработка и создание AI-агентов' : 'Видео о курсе'}
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
        />
      )}
    </div>
  );
};

export default CoursePage;
